import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useUploadQueueState() {
  const [mediaItemsState, setMediaItemsState] = useState([]);
  const [completionDialog, setCompletionDialog] = useState(null);
  const [selectionFeedback, setSelectionFeedback] = useState({ accepted: [], rejected: [] });
  const [pendingDeleteItemId, setPendingDeleteItemId] = useState(null);
  const [pendingTranscriptDeleteItemId, setPendingTranscriptDeleteItemId] = useState(null);
  const [localDeleteInProgress, setLocalDeleteInProgress] = useState(false);
  const [transcriptDeleteInProgress, setTranscriptDeleteInProgress] = useState(false);
  const [isUiLocked, setIsUiLocked] = useState(false);

  const mediaInputRef = useRef(null);
  const transcriptInputRefs = useRef({});
  const mediaItemsRef = useRef([]);

  useEffect(() => {
    mediaItemsRef.current = mediaItemsState;
  }, [mediaItemsState]);

  const setMediaItems = useCallback((updater) => {
    setMediaItemsState((currentItems) => {
      const nextItems = typeof updater === 'function'
        ? updater(currentItems)
        : updater;
      const normalizedNextItems = Array.isArray(nextItems) ? nextItems : [];
      mediaItemsRef.current = normalizedNextItems;
      return normalizedNextItems;
    });
  }, []);

  const updateMediaItem = useCallback((itemId, updater) => {
    setMediaItems((currentItems) => currentItems.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      return updater(item);
    }));
  }, [setMediaItems]);

  const updateItemStatus = useCallback((itemId, statusPatch) => {
    updateMediaItem(itemId, (item) => {
      const nextItem = {
        ...item,
        ...statusPatch
      };

      if (typeof statusPatch?.progress === 'number') {
        // Progress must never move backwards during the same upload run.
        // Once a file upload reaches 100%, keep the bar full while the scan
        // status continues to be refreshed. A new upload run resets progress
        // to 0 explicitly before this helper is used again.
        const currentProgress = Number.isFinite(item.progress) ? item.progress : 0;
        nextItem.progress = Math.max(currentProgress, Math.min(100, Math.max(0, statusPatch.progress)));
      }

      return nextItem;
    });
  }, [updateMediaItem]);

  const setTranscriptInputRef = useCallback((item, element) => {
    if (!item?.id) {
      return;
    }

    transcriptInputRefs.current[item.id] = element;
  }, []);

  return {
    mediaItems: mediaItemsState,
    setMediaItems,
    mediaItemsRef,
    completionDialog,
    setCompletionDialog,
    selectionFeedback,
    setSelectionFeedback,
    pendingDeleteItemId,
    setPendingDeleteItemId,
    pendingTranscriptDeleteItemId,
    setPendingTranscriptDeleteItemId,
    localDeleteInProgress,
    setLocalDeleteInProgress,
    transcriptDeleteInProgress,
    setTranscriptDeleteInProgress,
    isUiLocked,
    setIsUiLocked,
    mediaInputRef,
    transcriptInputRefs,
    setTranscriptInputRef,
    updateMediaItem,
    updateItemStatus
  };
}

export function useUploadQueueDerivedModel({
  isReadOnly,
  isUiLocked,
  mediaItems,
  caseFiles,
  inlineError,
  setInlineError,
  caseUploadBlockedMessage,
  selectionFeedback,
  acknowledged,
  getUploadLimitTotals,
  getUploadLimitValidationMessage,
  shouldCountLocalUploadItem,
  hasPendingLocalScanStatus,
  shouldShowUploadQueueItem,
  isLocalUploadFailed,
  isLocalUploadSuccessful,
  isLocalUploadInProgress,
  hasPendingAnyScanWork,
  hasVirusDetectedInCaseFiles,
  isVirusStatus,
  virusBlockMessage,
  isStaleInlineValidationMessage,
  getMediaFileValidationMessage,
  getTranscriptFileValidationMessage
}) {
  const interactionLocked = isReadOnly || isUiLocked;

  const totalSelectedBytes = useMemo(() => mediaItems.reduce((total, item) => {
    const mediaSize = item.mediaFile?.size ?? 0;
    const transcriptSize = item.transcriptFile?.size ?? 0;
    return total + mediaSize + transcriptSize;
  }, 0), [mediaItems]);

  const uploadLimitTotals = useMemo(() => getUploadLimitTotals(mediaItems, caseFiles), [caseFiles, getUploadLimitTotals, mediaItems]);
  const totalMediaCount = uploadLimitTotals.totalMediaCount;

  const validationMessage = useMemo(() => {
    if (caseUploadBlockedMessage) {
      return caseUploadBlockedMessage;
    }

    const limitMessage = getUploadLimitValidationMessage(mediaItems, caseFiles);
    if (limitMessage) {
      return limitMessage;
    }

    if (selectionFeedback.rejected.length > 0) {
      return '';
    }

    const hideInlineValidationMessages = new Set([
      getTranscriptFileValidationMessage?.({ name: 'placeholder.pdf', size: 1 }),
      getMediaFileValidationMessage?.({ name: 'placeholder.mp4', size: 1 }),
      getTranscriptFileValidationMessage?.({ name: 'placeholder.pdf', size: Number.MAX_SAFE_INTEGER }),
      getMediaFileValidationMessage?.({ name: 'placeholder.mp4', size: Number.MAX_SAFE_INTEGER })
    ].filter(Boolean));

    if (hideInlineValidationMessages.has(inlineError)) {
      return '';
    }

    return inlineError;
  }, [caseFiles, caseUploadBlockedMessage, getUploadLimitValidationMessage, inlineError, mediaItems, selectionFeedback.rejected.length, getTranscriptFileValidationMessage, getMediaFileValidationMessage]);

  const hasLocalVirusDetected = useMemo(() => mediaItems
    .filter((item) => shouldCountLocalUploadItem(item, caseFiles))
    .some((item) => isVirusStatus(item.scanStatus) || isVirusStatus(item.transcriptScanStatus)), [caseFiles, isVirusStatus, mediaItems, shouldCountLocalUploadItem]);

  const hasRemoteVirusDetected = useMemo(() => (caseFiles ?? []).some((record) => isVirusStatus(record?.scanStatus ?? record?.ScanStatus)), [caseFiles, isVirusStatus]);
  const hasVirusDetected = hasLocalVirusDetected || hasRemoteVirusDetected;
  const hasPendingScanWorkNow = useMemo(() => hasPendingAnyScanWork(mediaItems, caseFiles), [caseFiles, hasPendingAnyScanWork, mediaItems]);

  useEffect(() => {
    if (!inlineError || !isStaleInlineValidationMessage(inlineError)) {
      return;
    }

    if (inlineError === virusBlockMessage) {
      if (!hasVirusDetected) {
        setInlineError('');
      }
      return;
    }

    const currentLimitMessage = getUploadLimitValidationMessage(mediaItems, caseFiles);
    if (!currentLimitMessage || currentLimitMessage !== inlineError) {
      setInlineError('');
    }
  }, [caseFiles, getUploadLimitValidationMessage, hasVirusDetected, inlineError, isStaleInlineValidationMessage, mediaItems, setInlineError, virusBlockMessage]);

  const pendingUploadItems = useMemo(() => mediaItems.filter((item) => shouldCountLocalUploadItem(item, caseFiles) || hasPendingLocalScanStatus(item)), [caseFiles, hasPendingLocalScanStatus, mediaItems, shouldCountLocalUploadItem]);
  const uploadQueueItems = useMemo(() => mediaItems.filter((item) => shouldShowUploadQueueItem(item)), [mediaItems, shouldShowUploadQueueItem]);
  const localUploadingCount = useMemo(() => mediaItems.filter((item) => item?.mediaUploadStatus === 'uploading' || item?.transcriptUploadStatus === 'uploading').length, [mediaItems]);
  const localSelectedNotUploadedCount = useMemo(() => mediaItems.filter((item) => shouldCountLocalUploadItem(item, caseFiles) && item?.mediaUploadStatus !== 'uploading').length, [caseFiles, mediaItems, shouldCountLocalUploadItem]);
  const visibleUploadItems = uploadQueueItems;
  const hasFailedUploadQueueItems = useMemo(() => uploadQueueItems.some((item) => isLocalUploadFailed(item)), [isLocalUploadFailed, uploadQueueItems]);
  const failedUploadQueuePanelStyle = hasFailedUploadQueueItems
    ? { borderColor: '#fdba74', background: '#fff7ed' }
    : undefined;

  const completionSummary = useMemo(() => {
    if (mediaItems.length === 0) {
      let overallStatus = 'idle';
      if (validationMessage) {
        overallStatus = 'error';
      } else if (hasVirusDetected) {
        overallStatus = 'blocked';
      }

      return {
        overallStatus,
        readyToSubmit: false,
        canUpload: false,
        hasVirus: hasVirusDetected,
        completedCount: 0,
        errorCount: 0,
        pendingCount: 0
      };
    }

    const itemSummaries = mediaItems.map((item) => {
      const mediaVirus = isVirusStatus(item.scanStatus);
      const transcriptVirus = isVirusStatus(item.transcriptScanStatus);
      const hasError = isLocalUploadFailed(item);
      const isComplete = isLocalUploadSuccessful(item);
      const isInProgress = isLocalUploadInProgress(item);

      return {
        id: item.id,
        mediaFileName: item.mediaFile?.name ?? '',
        mediaStoredFileName: item.mediaStoredFileName || '',
        transcriptFileName: item.transcriptFile?.name ?? '',
        transcriptStoredFileName: item.transcriptStoredFileName || '',
        mediaUploadStatus: item.mediaUploadStatus,
        mediaScanStatus: item.scanStatus,
        transcriptUploadStatus: item.transcriptUploadStatus,
        transcriptScanStatus: item.transcriptScanStatus,
        mediaId: item.mediaId,
        transcriptFileId: item.transcriptFileId,
        progress: item.progress,
        hasVirus: mediaVirus || transcriptVirus,
        hasError,
        isInProgress,
        isComplete,
        errorMessage: item.latestError || ''
      };
    });

    const hasVirus = hasVirusDetected;
    const hasErrors = itemSummaries.some((item) => item.hasError);
    const allComplete = itemSummaries.every((item) => item.isComplete);
    const canUpload = acknowledged && pendingUploadItems.length > 0 && !interactionLocked && !validationMessage && !hasVirus;
    const readyToSubmit = allComplete && !hasErrors && acknowledged && !interactionLocked && !validationMessage && !hasVirus;
    const pendingCount = itemSummaries.filter((item) => item.isInProgress).length;
    const errorCount = itemSummaries.filter((item) => item.hasError).length;

    let overallStatus = 'idle';
    if (validationMessage) {
      overallStatus = 'error';
    } else if (hasErrors) {
      overallStatus = 'error';
    } else if (interactionLocked) {
      overallStatus = 'uploading';
    } else if (hasVirus) {
      overallStatus = 'blocked';
    } else if (readyToSubmit) {
      overallStatus = 'ready';
    } else if (pendingCount > 0) {
      overallStatus = 'waiting';
    }

    return {
      overallStatus,
      readyToSubmit,
      canUpload,
      hasVirus,
      completedCount: itemSummaries.filter((item) => item.isComplete).length,
      errorCount,
      pendingCount,
      itemSummaries
    };
  }, [acknowledged, hasVirusDetected, interactionLocked, isLocalUploadFailed, isLocalUploadInProgress, isLocalUploadSuccessful, isVirusStatus, mediaItems, pendingUploadItems.length, validationMessage]);

  return {
    interactionLocked,
    totalSelectedBytes,
    totalMediaCount,
    validationMessage,
    hasVirusDetected,
    hasPendingScanWorkNow,
    pendingUploadItems,
    uploadQueueItems,
    localUploadingCount,
    localSelectedNotUploadedCount,
    visibleUploadItems,
    failedUploadQueuePanelStyle,
    completionSummary
  };
}
